import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";

/**
 * AssigneeCombobox — searchable single-select dropdown over /api/team.
 *
 * Extracted from TaskForm so both the new-task page and the TaskDetail
 * property rail can use the same picker (T30). Self-contained; no UI lib.
 *
 * Behaviour:
 *   - Trigger row shows the current assignee (avatar + name) or "Unassigned".
 *   - Clicking the trigger opens a panel with a search box + scrollable
 *     member list, filtered by case-insensitive name/email substring.
 *   - Click a row to select; click again on the same row (or "Unassigned"
 *     at the top) to clear.
 *   - Escape closes the panel without changing selection.
 *   - Click-outside also closes the panel.
 */

export interface AssigneeMember {
  id: string;
  name?: string | null;
  email?: string | null;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return (
    parts
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("")
      .slice(0, 2) || "?"
  );
}

export function AssigneeCombobox({
  members,
  loading,
  value,
  onChange,
}: {
  members: AssigneeMember[];
  loading: boolean;
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selected = value ? (members.find((m) => m.id === value) ?? null) : null;
  const displayOf = (m: AssigneeMember) => m.name ?? m.email ?? m.id;

  const filtered = (() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const hay = `${m.name ?? ""} ${m.email ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  })();

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus the search input when the panel opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (loading) {
    return <span className="t-xs t-muted">Loading…</span>;
  }
  if (members.length === 0) {
    return <span className="t-xs t-muted">No teammates yet.</span>;
  }

  return (
    <div
      className="assignee-combo"
      ref={rootRef}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="assignee-combo-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? (
          <>
            <span className="ap on" aria-hidden>
              {initialsOf(displayOf(selected))}
            </span>
            <span className="assignee-combo-name">{displayOf(selected)}</span>
          </>
        ) : (
          <span className="assignee-combo-placeholder">Unassigned</span>
        )}
        <Icon name="chev" />
      </button>
      {open && (
        <div className="assignee-combo-panel" role="listbox">
          <input
            ref={inputRef}
            className="assignee-combo-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search teammates…"
            aria-label="Search teammates"
          />
          <div className="assignee-combo-list">
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              className={`assignee-combo-row${value === null ? " on" : ""}`}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <span className="ap" aria-hidden>
                ?
              </span>
              <span className="assignee-combo-name">Unassigned</span>
            </button>
            {filtered.map((m) => {
              const on = m.id === value;
              const name = displayOf(m);
              return (
                <button
                  key={m.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`assignee-combo-row${on ? " on" : ""}`}
                  onClick={() => {
                    onChange(on ? null : m.id);
                    setOpen(false);
                  }}
                >
                  <span className="ap on" aria-hidden>
                    {initialsOf(name)}
                  </span>
                  <span className="assignee-combo-name">{name}</span>
                  {m.email && m.name && (
                    <span className="assignee-combo-email t-muted">
                      {m.email}
                    </span>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="assignee-combo-empty t-xs t-muted">No match.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
